import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);
  const isHomePage = location.pathname === '/';

  const getNavLinkClass = (isActive) => {
    const base = "font-semibold text-sm tracking-wider uppercase transition-all duration-200 pb-1";
    if (isActive) {
      if (!isHomePage && !isScrolled) {
        return `${base} text-white`;
      }
      return `${base} text-black`;
    } else {
      if (isScrolled) {
        return `${base} text-gray-700 hover:text-black`;
      } else if (isHomePage) {
        return `${base} text-gray-700 hover:text-black`;
      } else {
        return `${base} text-white/80 hover:text-white`;
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
  }, [location]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 250); // 250ms delay
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isOpen
          ? 'bg-white border-b border-gray-100 py-3 shadow-md'
          : 'bg-transparent border-b border-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex flex-col gap-3">
        {/* Top Row: Logo, CTAs & Mobile Toggle */}
        <div className="flex items-center justify-between w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/logo2_3.png"
              alt="Thai Hot Chili Logo"
              className="h-20 md:h-26 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Right Side: Desktop CTAs & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            {/* Desktop CTA Buttons (Visible above 1200px) */}
            <div className="hidden min-[1201px]:flex items-center gap-4">
              <a
                href="tel:+4552901306"
                className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase bg-brand-primary hover:bg-brand-primary/90 text-white px-4 py-2.5 rounded-full transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                <Phone className="w-3.5 h-3.5" />
                Ring Foodtruck
              </a>
              <a
                href="mailto:jes.valente@gmail.com"
                className={`flex items-center gap-2 text-xs font-semibold tracking-wider uppercase border px-4 py-2.5 rounded-full transition-all ${
                  isScrolled || isHomePage
                    ? 'bg-black text-white hover:bg-black border-black shadow-md'
                    : 'border-white hover:bg-transparent text-white'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                Send Email
              </a>
            </div>

            {/* Mobile Menu Button (Visible below 1200px) */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`min-[1201px]:hidden transition-colors p-2 ${
                isScrolled || isOpen 
                  ? 'text-gray-800 hover:text-black' 
                  : isHomePage
                    ? 'text-brand-dark hover:text-black'
                    : 'text-white hover:text-white/80'
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Bottom Row: Desktop Navigation (Visible above 1200px) */}
        <nav className="hidden min-[1201px]:flex items-center gap-8 self-start mt-1">
          <NavLink
            to="/"
            className={({ isActive }) => getNavLinkClass(isActive)}
          >
            Forside
          </NavLink>

          {/* Dropdown for Menukort */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`flex items-center gap-1 font-semibold text-sm tracking-wider uppercase transition-all duration-200 pb-1 ${
                location.pathname.startsWith('/menukort') 
                  ? isScrolled || isHomePage
                    ? 'text-black' 
                    : 'text-white'
                  : (isScrolled || isHomePage ? 'text-gray-700 hover:text-black' : 'text-white/80 hover:text-white')
              }`}
            >
              Menukort
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            <div
              className={`absolute top-full left-0 mt-2 w-48 bg-white border border-gray-100 rounded-lg shadow-xl py-2 transition-all duration-200 origin-top-left ${
                isDropdownOpen
                  ? 'opacity-100 scale-100 pointer-events-auto'
                  : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              <Link
                to="/menukort"
                className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-black transition-colors font-semibold tracking-wider uppercase"
              >
                Hele Menukortet
              </Link>
              <Link
                to="/menukort/forretter"
                className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-black transition-colors font-semibold tracking-wider uppercase"
              >
                Forretter
              </Link>
              <Link
                to="/menukort/hovedretter"
                className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-black transition-colors font-semibold tracking-wider uppercase"
              >
                Hovedretter
              </Link>
              <Link
                to="/menukort/tilbehoer"
                className="block px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-black transition-colors font-semibold tracking-wider uppercase"
              >
                Tilbehør
              </Link>
            </div>
          </div>

          <NavLink
            to="/foodtruck-catering"
            className={({ isActive }) => getNavLinkClass(isActive)}
          >
            Foodtruck & Catering
          </NavLink>

          <NavLink
            to="/galleri"
            className={({ isActive }) => getNavLinkClass(isActive)}
          >
            Galleri
          </NavLink>

          <NavLink
            to="/om-os"
            className={({ isActive }) => getNavLinkClass(isActive)}
          >
            Om Os
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) => getNavLinkClass(isActive)}
          >
            Kontakt
          </NavLink>
        </nav>
      </div>

      {/* Mobile Navigation Drawer (Visible below 1200px) */}
      <div
        className={`absolute top-full left-0 right-0 bg-white z-40 transition-transform duration-300 min-[1201px]:hidden border-t border-gray-100 shadow-xl h-[calc(100vh-100%)] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full px-6 py-8 overflow-y-auto">
          <nav className="flex flex-col gap-6 text-base font-medium">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `transition-colors hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase ${
                  isActive ? 'text-black font-bold' : 'text-gray-800'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Menukort Expandable */}
            <div className="flex flex-col">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between text-gray-800 hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase font-medium"
              >
                <span>Menukort</span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`flex flex-col pl-4 gap-4 overflow-hidden transition-all duration-200 ${
                  isDropdownOpen ? 'max-h-40 mt-4 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <Link to="/menukort" className="text-gray-500 hover:text-black transition-colors text-sm tracking-wider uppercase font-medium">
                  Hele Menukortet
                </Link>
                <Link to="/menukort/forretter" className="text-gray-500 hover:text-black transition-colors text-sm tracking-wider uppercase font-medium">
                  Forretter
                </Link>
                <Link to="/menukort/hovedretter" className="text-gray-500 hover:text-black transition-colors text-sm tracking-wider uppercase font-medium">
                  Hovedretter
                </Link>
                <Link to="/menukort/tilbehoer" className="text-gray-500 hover:text-black transition-colors text-sm tracking-wider uppercase font-medium">
                  Tilbehør
                </Link>
              </div>
            </div>

            <NavLink
              to="/foodtruck-catering"
              className={({ isActive }) =>
                `transition-colors hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase ${
                  isActive ? 'text-black font-bold' : 'text-gray-800'
                }`
              }
            >
              Foodtruck & Catering
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `transition-colors hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase ${
                  isActive ? 'text-black font-bold' : 'text-gray-800'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `transition-colors hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase ${
                  isActive ? 'text-black font-bold' : 'text-gray-800'
                }`
              }
            >
              Om Os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `transition-colors hover:text-black py-2 border-b border-gray-100 tracking-wider uppercase ${
                  isActive ? 'text-black font-bold' : 'text-gray-800'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Mobile CTA Buttons */}
          <div className="flex flex-col gap-4 mt-12">
            <a
              href="tel:+4552901306"
              className="flex items-center justify-center gap-2 text-sm font-semibold tracking-wider uppercase bg-brand-primary text-white py-3.5 rounded-full shadow-md"
            >
              <Phone className="w-4 h-4" />
              Ring Foodtruck (52 90 13 06)
            </a>
            <a
              href="mailto:jes.valente@gmail.com"
              className="flex items-center justify-center gap-2 text-sm font-semibold tracking-wider uppercase border border-gray-200 text-gray-700 py-3.5 rounded-full"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
